import { ErrorModal } from "@dexteel/mesf-core";
import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { FieldError } from "react-hook-form";

import { Grade } from "../models/Grade";
import { getGrades } from "../repositories/GradeRepository";

type UseGradesProps = {
	includeInactive?: boolean;
	refetchInterval?: number;
};

export const useGrades = ({
	includeInactive = false,
	refetchInterval,
}: UseGradesProps = {}) => {
	return useQuery<Grade[], Error>({
		queryKey: ["grades", includeInactive],
		meta: { realtime: ["M2.GetGrades"] },
		queryFn: ({ signal }) => getGrades({ includeInactive }, signal),
		refetchInterval,
	});
};

type Props = {
	label?: string;
	value: number | null;
	onChange?: (value: number | null, name?: string | null) => void;
	disabled?: boolean;
	required?: boolean;
	fieldError?: FieldError;
	disabledGradeIds?: number[];
	allOptionLabel?: string;
};

type GradeOption = { GradeId: number | null; GradeName: string };

const allOptions = new Map<string, GradeOption>();

const getAllOption = (label: string) => {
	const existing = allOptions.get(label);
	if (existing) return existing;
	const option = { GradeId: null, GradeName: label };
	allOptions.set(label, option);
	return option;
};

export const GradeSelector = ({
	label = "Grade",
	value,
	onChange = () => {},
	disabled = false,
	required = false,
	fieldError,
	disabledGradeIds,
	allOptionLabel,
}: Props) => {
	const [error, setError] = useState("");

	const {
		data: grades,
		isLoading,
		error: e,
		isError,
	} = useGrades({
		refetchInterval: 60_000,
	});

	const allOption = allOptionLabel ? getAllOption(allOptionLabel) : null;
	const options: GradeOption[] = [
		...(allOption ? [allOption] : []),
		...(grades ?? []),
	];
	const selectedValue = options.find((o) => o.GradeId === value) ?? null;

	useEffect(() => {
		if (isError && e) {
			setError(e.message);
		}
	}, [e, isError]);

	return (
		<>
			<Autocomplete
				size="small"
				clearOnEscape
				autoHighlight
				options={options}
				getOptionLabel={(option) => option.GradeName}
				getOptionDisabled={(option) =>
					option.GradeId != null && !!disabledGradeIds?.includes(option.GradeId)
				}
				isOptionEqualToValue={(option, v) => option.GradeId === v.GradeId}
				disabled={disabled}
				value={selectedValue}
				onChange={(_event, newValue) => {
					onChange(
						newValue?.GradeId ?? null,
						newValue?.GradeId == null ? null : newValue.GradeName,
					);
				}}
				renderInput={(params) => (
					<TextField
						{...params}
						required={required}
						label={label}
						error={!!fieldError}
						helperText={fieldError?.message}
						margin="none"
						sx={{
							backgroundColor: "white",
						}}
						inputProps={{
							...params.inputProps,
							"data-test-id": "grade-selector",
						}}
						InputProps={{
							...params.InputProps,
							endAdornment: (
								<>
									{isLoading ? (
										<CircularProgress color="inherit" size={20} />
									) : (
										params.InputProps.endAdornment
									)}
								</>
							),
						}}
					/>
				)}
			/>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading grades"
			/>
		</>
	);
};
